import type { InputHTMLAttributes, ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: string;
  error?: string | null;
  hint?: string;
  trailing?: ReactNode;
}

export default function Input({
  label,
  icon,
  error,
  hint,
  trailing,
  id,
  className = '',
  ...rest
}: InputProps) {
  const inputId = id || rest.name || label?.replace(/\s+/g, '-').toLowerCase();

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label"
        >
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {icon && (
          <span className="pointer-events-none absolute left-3 w-4 h-4 flex items-center justify-center text-foreground-400">
            <i className={`${icon} text-base`}></i>
          </span>
        )}
        <input
          id={inputId}
          className={[
            'h-11 w-full rounded-md border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400',
            'transition-colors duration-200 focus:outline-none focus:ring-2',
            icon ? 'pl-10' : 'pl-3.5',
            trailing ? 'pr-11' : 'pr-3.5',
            error
              ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
              : 'border-background-300 focus:border-primary-400 focus:ring-primary-200',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />
        {trailing && <span className="absolute right-2 flex items-center">{trailing}</span>}
      </div>
      {error ? (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
          <i className="ri-error-warning-line text-sm"></i>
          {error}
        </p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-foreground-500">{hint}</p>
      )}
    </div>
  );
}